/**
 * Central keyboard-shortcut registry.
 *
 * One window-level keydown listener dispatches to declaratively registered
 * bindings. Bindings carry a scope; scopes form a stack (pushed by UI that
 * takes keyboard focus, e.g. the clip viewer or a fullscreen overlay). On a
 * keypress the topmost scope with a matching binding wins; 'global' bindings
 * are the fallback when no pushed scope matches.
 *
 * Key descriptors: `[Mod+][Alt+][Shift+]Key` where Mod = ⌘ or Ctrl and Key is
 * `Space`, an `e.key` name (`ArrowLeft`, `Home`, `Escape`…) or a single
 * character. Single-character keys match the produced character (so `?` and
 * `+` work without spelling out Shift) and are case-insensitive.
 */

export type KeyScope = 'global' | 'timeline' | 'clip-viewer' | 'search-overlay';

export interface KeyBinding {
  id: string; // e.g. 'transport.playPause'
  keys: string[]; // e.g. ['Space'], ['Mod+Z'], ['Shift+ArrowLeft'], ['?']
  scope: KeyScope;
  description: string; // shown in the shortcuts help overlay
  group: string; // help-overlay section
  allowInInputs?: boolean; // default false
  /**
   * Stand aside when the user has selected text, letting the browser's native
   * handling run instead. For the clipboard bindings: `isEditableTarget` only
   * exempts inputs, so Mod+C over selected text in a plain `<div>` — a chat
   * message, an item description, a search result — was swallowed and copied
   * the selected CLIP instead. Nothing indicated why; right-click → Copy still
   * worked, which makes it read as a broken keyboard rather than a shortcut
   * conflict.
   */
  yieldToTextSelection?: boolean; // default false
  run(e: KeyboardEvent): void;
}

const bindings: KeyBinding[] = [];
const scopeStack: KeyScope[] = [];

export function registerBindings(list: KeyBinding[]): () => void {
  bindings.push(...list);
  return () => {
    for (const b of list) {
      const i = bindings.indexOf(b);
      if (i >= 0) bindings.splice(i, 1);
    }
  };
}

export function pushScope(scope: KeyScope): () => void {
  scopeStack.push(scope);
  return () => {
    const i = scopeStack.lastIndexOf(scope);
    if (i >= 0) scopeStack.splice(i, 1);
  };
}

export function activeScopes(): KeyScope[] {
  return [...scopeStack];
}

/** All registered bindings (for the help overlay), registration order. */
export function allBindings(): KeyBinding[] {
  return [...bindings];
}

/** Normalize a KeyboardEvent to a descriptor comparable with KeyBinding.keys. */
export function eventDescriptor(e: KeyboardEvent): string {
  let key = e.key;
  if (e.code === 'Space') key = 'Space';
  const single = key.length === 1;
  if (single) key = key.toUpperCase();
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push('Mod');
  if (e.altKey) parts.push('Alt');
  // Symbols already reflect the shift layer ('?', '+'), so Shift is dropped
  // for them; letters and named keys keep it (Mod+Shift+Z ≠ Mod+Z).
  const shiftEncodedInChar = single && key.toUpperCase() === key.toLowerCase();
  if (e.shiftKey && !shiftEncodedInChar) parts.push('Shift');
  parts.push(key);
  return parts.join('+');
}

function normalizeDescriptor(d: string): string {
  const parts = d.split('+');
  // Preserve a literal trailing '+' key (e.g. '+', 'Mod++').
  const key = d.endsWith('+') ? '+' : parts.pop()!;
  const mods = parts.filter((p) => p !== '' && p !== key);
  const has = (m: string) => mods.includes(m);
  const out: string[] = [];
  if (has('Mod')) out.push('Mod');
  if (has('Alt')) out.push('Alt');
  if (has('Shift')) out.push('Shift');
  out.push(key.length === 1 ? key.toUpperCase() : key);
  return out.join('+');
}

function isEditableTarget(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable;
}

function findBinding(desc: string, inInput: boolean): KeyBinding | null {
  const matches = (b: KeyBinding) =>
    (!inInput || b.allowInInputs) && b.keys.some((k) => normalizeDescriptor(k) === desc);
  // Topmost pushed scope wins; 'global' is the fallback.
  for (let i = scopeStack.length - 1; i >= 0; i--) {
    const scope = scopeStack[i];
    for (let j = bindings.length - 1; j >= 0; j--) {
      const b = bindings[j];
      if (b.scope === scope && matches(b)) return b;
    }
  }
  for (let j = bindings.length - 1; j >= 0; j--) {
    const b = bindings[j];
    if (b.scope === 'global' && matches(b)) return b;
  }
  return null;
}

/** True when the user has an actual (non-collapsed) text selection on the page. */
function hasTextSelection(): boolean {
  if (typeof window === 'undefined' || !window.getSelection) return false;
  const sel = window.getSelection();
  // `isCollapsed` covers the common "caret, nothing selected" case; the
  // toString() check guards selections that span only zero-width content.
  return !!sel && !sel.isCollapsed && sel.toString().trim().length > 0;
}

export function handleKeyEvent(e: KeyboardEvent): boolean {
  const b = findBinding(eventDescriptor(e), isEditableTarget(e));
  if (!b) return false;
  if (b.yieldToTextSelection && hasTextSelection()) return false;
  e.preventDefault();
  b.run(e);
  return true;
}

let installed = 0;
const listener = (e: KeyboardEvent) => {
  handleKeyEvent(e);
};

/** Install the single window keydown listener (ref-counted for React strict mode). */
export function installKeymap(): () => void {
  if (installed === 0) window.addEventListener('keydown', listener);
  installed++;
  return () => {
    installed--;
    if (installed === 0) window.removeEventListener('keydown', listener);
  };
}

/** Test hook. */
export function __resetKeymap(): void {
  bindings.length = 0;
  scopeStack.length = 0;
}
